import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { useCatalog } from '@/features/catalog/context';

import { catalogKeys } from '../api/catalogKeys';
import { catalogRetry, catalogRetryDelay, catalogStaleTime } from '../api/catalogQueryOptions';
import type {
  FeaturedProductListInput,
  PublicCategoryListInput,
  PublicProductListInput,
} from '../api/types';

const retryOptions = { retry: catalogRetry, retryDelay: catalogRetryDelay };

export function usePublicCategories(input: PublicCategoryListInput = {}) {
  const { service } = useCatalog();
  return useQuery({
    queryKey: catalogKeys.categoryList(input),
    queryFn: ({ signal }) => service.listCategories(input, signal),
    staleTime: catalogStaleTime.categories,
    ...retryOptions,
  });
}

export function usePublicCategory(slug: string | undefined) {
  const { service } = useCatalog();
  return useQuery({
    queryKey: catalogKeys.category(slug ?? ''),
    queryFn: ({ signal }) => service.getCategory(slug!, signal),
    enabled: Boolean(slug),
    staleTime: catalogStaleTime.categories,
    ...retryOptions,
  });
}

export function usePublicProducts(input: Omit<PublicProductListInput, 'cursor'> = {}) {
  const { service } = useCatalog();
  return useInfiniteQuery({
    queryKey: catalogKeys.productList(input),
    queryFn: ({ pageParam, signal }) =>
      service.listProducts({ ...input, ...(pageParam ? { cursor: pageParam } : {}) }, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    staleTime: catalogStaleTime.products,
    ...retryOptions,
  });
}

export function useCategoryProducts(
  categorySlug: string | undefined,
  input: Omit<PublicProductListInput, 'category' | 'cursor'> = {},
) {
  const { service } = useCatalog();
  return useInfiniteQuery({
    queryKey: catalogKeys.categoryProducts(categorySlug ?? '', input),
    queryFn: ({ pageParam, signal }) =>
      service.listCategoryProducts(
        categorySlug!,
        { ...input, ...(pageParam ? { cursor: pageParam } : {}) },
        signal,
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: Boolean(categorySlug),
    staleTime: catalogStaleTime.products,
    ...retryOptions,
  });
}

export function usePublicProduct(slug: string | undefined) {
  const { service } = useCatalog();
  return useQuery({
    queryKey: catalogKeys.product(slug ?? ''),
    queryFn: ({ signal }) => service.getProduct(slug!, signal),
    enabled: Boolean(slug),
    staleTime: catalogStaleTime.product,
    ...retryOptions,
  });
}

export function useFeaturedProducts(input: FeaturedProductListInput = {}) {
  const { service } = useCatalog();
  return useQuery({
    queryKey: catalogKeys.featuredProducts(input),
    queryFn: ({ signal }) => service.listFeaturedProducts(input, signal),
    staleTime: catalogStaleTime.featured,
    ...retryOptions,
  });
}
